// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 10-security-compliance.md#local-dev-cli-session
// @greenlight-spec: decisions/0028-agent-cli-mcp-independent-client.md
import * as React from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ChevronDown, ShieldAlert } from 'lucide-react';

import { Logo } from '@/components/greenlight/Logo';
import { Eyebrow } from '@/components/greenlight/Eyebrow';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import type { CliApprovalResult } from '@greenlight/contract';

/**
 * The browser half of plugin sign-in (ADR-0028). A person lands here with a code
 * their coding agent printed, because the agent could not approve it over MCP.
 *
 * One screen: the only question this persona can answer is whether the code came
 * from their own agent, so that question is the page's security content and it
 * sits above the code field rather than after it. Requester context is recorded
 * for investigation but never shown here — a source IP, an unverifiable
 * self-reported label, and a version string are not things a citizen developer
 * can triage (docs/10 §Local-dev CLI session).
 *
 * Rendered inside the authenticated shell's route tree but with its own focused
 * layout, like `/cli/done`: the person may be a citizen developer who never opens
 * the dashboard, and this is one task, not a page to navigate.
 */
export function CliApprove(): React.ReactElement {
  const location = useLocation();
  const { user, loading, unauthenticated } = useAuth();

  const [code, setCode] = React.useState('');
  const [approved, setApproved] = React.useState<CliApprovalResult | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const approve = api.cli.approve.useMutation();

  React.useEffect(() => {
    document.title = 'Approve plugin sign-in · Greenlight';
    return () => {
      document.title = 'Greenlight';
    };
  }, []);

  // Approval is the page's one context change, so it is the one thing focus
  // moves for: a screen reader announces the outcome instead of leaving focus on
  // a button that no longer exists.
  const doneHeadingRef = React.useRef<HTMLHeadingElement>(null);
  React.useEffect(() => {
    if (approved) doneHeadingRef.current?.focus();
  }, [approved]);

  if (loading) {
    return (
      <CliApproveFrame>
        <div className="space-y-3" aria-busy="true" aria-label="Loading">
          <Skeleton className="mx-auto h-3 w-24" />
          <Skeleton className="mx-auto h-6 w-52" />
          <Skeleton className="h-11 w-full" />
        </div>
      </CliApproveFrame>
    );
  }

  // A code-free `return_to`: the code lives in a form field, never the URL, so
  // the SSO round-trip cannot leak it into history, Referer, or a request log.
  if (unauthenticated) {
    return <Navigate to={`/login?return_to=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // Identity did not resolve and it was not a confirmed 401 — a transient 5xx or
  // network failure, which AuthProvider deliberately does not treat as signed-out
  // so the SPA cannot loop on /auth/login. Offer a retry rather than a code form
  // that would 401 the moment it is submitted.
  if (!user) {
    return (
      <CliApproveFrame>
        <StateIcon tone="danger">
          <AlertTriangle className="size-6 text-gl-red-text" aria-hidden />
        </StateIcon>
        <Eyebrow>Greenlight plugin</Eyebrow>
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
          Could not confirm who you are
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Greenlight could not load your account just now, so there is nothing to approve with. Your
          sign-in code is still good until it expires.
        </p>
        <Button onClick={() => window.location.reload()} className="mt-6 h-11 w-full md:h-9">
          Try again
        </Button>
      </CliApproveFrame>
    );
  }

  // Approval mints a credential carrying the approver's own access, so an
  // operator acting as someone else is refused before any code is entered — the
  // server refuses it too, and this only saves them the round trip.
  if (user.impersonating) {
    return (
      <CliApproveFrame>
        <StateIcon tone="danger">
          <ShieldAlert className="size-6 text-gl-red-text" aria-hidden />
        </StateIcon>
        <Eyebrow>Greenlight plugin</Eyebrow>
        <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
          Stop impersonating first
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          You are viewing Greenlight as{' '}
          <span className="font-medium text-foreground">{user.email}</span>. Approving here would
          give an agent that person&rsquo;s access, so it is not allowed while impersonating. Stop
          impersonating, then approve the code as yourself.
        </p>
        <Button asChild variant="outline" className="mt-6 h-11 w-full md:h-9">
          <Link to="/">Open dashboard</Link>
        </Button>
      </CliApproveFrame>
    );
  }

  if (approved) {
    return (
      <ApprovedScreen result={approved} approverEmail={user.email} headingRef={doneHeadingRef} />
    );
  }

  // Sent as typed, minus surrounding whitespace. The stored hash is over the
  // exact string the agent printed, so only the server knows which spellings
  // could name it; it folds case and, for a code printed without them,
  // separators. Normalising here instead would hide a punctuated code from that
  // lookup and skip the ambiguous-match refusal that keeps a handshake
  // registered under a variant spelling from binding in its place.
  const typedCode = code.trim();

  const confirm = (): void => {
    setError(null);
    approve.mutate(
      { body: { code: typedCode } },
      {
        onSuccess: (res) => {
          if (res.status === 200) setApproved(res.body as CliApprovalResult);
          else setError(messageFor(res));
        },
        onError: (err) => setError(messageFor(err)),
      },
    );
  };

  return (
    <CliApproveFrame>
      <Eyebrow>Greenlight plugin</Eyebrow>
      <h1 className="text-[22px] font-semibold leading-tight tracking-[-0.02em]">
        Sign in your coding agent
      </h1>

      <div className="mt-4 text-left">
        <p id="cli-approve-grant" className="text-sm leading-relaxed text-muted-foreground">
          Approving lets your coding agent use Greenlight as you: build and publish your apps, and
          reach the data you can reach.
        </p>

        <div className="mt-4 flex gap-2.5 rounded-gl border border-gl-amber/30 bg-gl-amber/10 p-3.5 text-left">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-gl-amber-text" aria-hidden />
          <p id="cli-approve-warning" className="text-[13px] leading-relaxed text-foreground">
            Only enter a code <span className="font-semibold">you or your agent created</span>.
            Nobody at your organization will ever send you a code to type here. If one arrived any
            other way, close this page.
          </p>
        </div>

        {error !== null && (
          <Alert variant="destructive" className="mt-4 text-left">
            <AlertTriangle className="size-4" aria-hidden />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="mt-5">
          <Label htmlFor="cli-code">Sign-in code from your agent</Label>
          <Input
            id="cli-code"
            name="code"
            autoFocus
            // The warning is the whole compensating control, and autofocus lands
            // past it — so a screen reader following focus would hear the field
            // label and never the sentence guarding it. Describing the input with
            // both means the grant and the provenance test are announced with it.
            aria-describedby="cli-approve-grant cli-approve-warning"
            autoComplete="off"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            // Enter must not approve: the confirm is a separate, deliberate
            // press, so the field is not wrapped in a submitting form and the
            // key is swallowed here rather than reaching a default action.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
            placeholder="XXXXXX"
            className="mt-1.5 h-11 font-mono md:h-9"
          />
        </div>

        <Button
          onClick={confirm}
          disabled={typedCode.length === 0 || approve.isPending}
          className="mt-4 h-11 w-full md:h-9"
        >
          {approve.isPending ? 'Approving…' : 'Approve sign-in'}
        </Button>
      </div>
    </CliApproveFrame>
  );
}

/**
 * Confirmation only. What approving grants was stated on the screen the person
 * was still deciding on; restating it here would read as a caution about
 * something they can no longer undo.
 *
 * The record behind the disclosure carries only the platform's own account of
 * the grant. The requester's self-reported CLI version is not in it: the create
 * call is unauthenticated, so in a phishing attempt the attacker chose that
 * string, and this panel is where the victim comes to work out what happened.
 */
function ApprovedScreen({
  result,
  approverEmail,
  headingRef,
}: {
  result: CliApprovalResult;
  approverEmail: string;
  headingRef: React.RefObject<HTMLHeadingElement>;
}): React.ReactElement {
  return (
    <CliApproveFrame>
      <StateIcon tone="primary">
        <CheckCircle2 className="size-6 text-verdant" aria-hidden />
      </StateIcon>
      <Eyebrow>Greenlight plugin</Eyebrow>
      <h1
        ref={headingRef}
        tabIndex={-1}
        className="text-[22px] font-semibold leading-tight tracking-[-0.02em] outline-none"
      >
        The Greenlight plugin is signed in
      </h1>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">You can close this tab.</p>

      <details className="group mt-6 overflow-hidden rounded-gl border border-border text-left">
        <summary
          className={cn(
            'flex cursor-pointer list-none items-center gap-2 px-3.5 py-3 text-[13px] font-medium text-foreground',
            'marker:content-none [&::-webkit-details-marker]:hidden',
            'hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          )}
        >
          <span className="min-w-0 flex-1">What was approved</span>
          <ChevronDown
            className="size-3.5 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden
          />
        </summary>
        <dl className="space-y-2.5 border-t border-border px-3.5 py-3">
          <Detail label="Signed in">Greenlight plugin</Detail>
          <Detail label="Approved">{approvedAtLabel(result.approved_at)}</Detail>
          <Detail label="Approved by">{approverEmail}</Detail>
        </dl>
      </details>

      <p className="mt-6 text-[13px] leading-relaxed text-muted-foreground">
        You can end this sign-in whenever you like — in{' '}
        <Link
          to="/settings?tab=sign-ins"
          className="font-medium text-foreground underline underline-offset-2 hover:text-link"
        >
          Settings → Plugin sign-ins
        </Link>
        .
      </p>

      <Button asChild variant="outline" className="mt-4 h-11 w-full md:h-9">
        <Link to="/">Open dashboard</Link>
      </Button>
    </CliApproveFrame>
  );
}

/** Local calendar spelling of the approval instant; the raw ISO stays in audit. */
function approvedAtLabel(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-[13px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="break-words font-medium">{children}</dd>
    </div>
  );
}

function StateIcon({
  tone,
  children,
}: {
  tone: 'primary' | 'danger';
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div
      className={`mx-auto mb-4 flex size-11 items-center justify-center rounded-full ${
        tone === 'primary' ? 'bg-glow' : 'bg-gl-red/10'
      }`}
    >
      {children}
    </div>
  );
}

/** The focused single-task frame `/cli/done` established, so the pair match. */
function CliApproveFrame({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-x-hidden bg-background px-5 py-12">
      <div
        aria-hidden
        className="pointer-events-none fixed -top-[200px] left-1/2 h-[600px] w-[900px] -translate-x-1/2 bg-[radial-gradient(circle,hsl(var(--signal)/0.14)_0%,transparent_62%)]"
      />
      <div className="fixed right-4 top-4 z-10">
        <ThemeToggle />
      </div>
      <main className="relative z-[1] flex w-full max-w-[408px] flex-col items-center">
        <div className="mb-7 flex flex-col items-center gap-2">
          <Logo className="h-7 text-foreground" />
          <div className="font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            by Shift Engineering
          </div>
        </div>
        <div
          aria-live="polite"
          className="w-full rounded-gl-lg border border-border bg-card p-6 text-center shadow-xl sm:p-8"
        >
          {children}
        </div>
      </main>
    </div>
  );
}

/** Envelope `message` + `next_steps` (docs/16), or a connection line when there is no body. */
function messageFor(errOrRes: unknown): string {
  if (errOrRes !== null && typeof errOrRes === 'object' && 'body' in errOrRes) {
    const body = (errOrRes as { body: unknown }).body;
    if (body !== null && typeof body === 'object') {
      const record = body as { message?: unknown; next_steps?: unknown };
      const message =
        typeof record.message === 'string' ? record.message : 'That code could not be approved.';
      return typeof record.next_steps === 'string' ? `${message} ${record.next_steps}` : message;
    }
    return 'That code could not be approved.';
  }
  return 'Could not reach Greenlight. Check your connection and try again.';
}
